import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import type { MaterialRate } from '~hooks/useMaterialRates/models';
import useMaterialRates from '~hooks/useMaterialRates/useMaterialRates';
import connection from '~services/connectionModule';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `project.id`, `company.id` and `account.id` (watched, via the
 *           Provider).
 * Writes:   the Provider's `projectMaterialRates` state — the read-only
 *           per-material rate lines the materials card renders under each
 *           additive material row, narrowed to the rates that apply to the
 *           order's customer account.
 * Guards:   needs both a project and a company. Every change to either clears
 *           the published rates first, so a stale project's prices are never
 *           shown under a new one. A request token discards a resolution the user has
 *           already navigated away from, so a slow response for project A can
 *           never repaint project B's rates. The customer is NOT a fetch
 *           trigger — it only narrows what the fetch already returned.
 * Legacy:   none.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("project material rates").
 */

const EMPTY_PROJECT_MATERIAL_RATES: MaterialRate[] = [];

/**
 * Keeps the rates that could price a load for this customer: the ones scoped to
 * that customer, plus the unscoped ones that apply to anybody.
 *
 * The material_rates index cannot do this narrowing for us. Passing
 * `filter[customer_account_id]` there returns ONLY exact matches, dropping every
 * unscoped rate on the project — so sending the filter would hide the project's
 * general pricing, which is strictly worse than showing all of it. (The typeahead
 * endpoint widens the same filter to include NULLs; the index does not.) This is
 * an applicability check on a value the record carries, not a re-implementation
 * of the backend's specificity ranking — which rate wins is still the backend's
 * call, and the card lists every candidate rather than guessing.
 */
function ratesApplicableToAccount(rates: MaterialRate[], accountId: string | undefined) {
  return rates.filter((rate) => {
    const scopedToAccountId = rate.customerAccount?.id;

    if (!scopedToAccountId) {
      return true;
    }

    return scopedToAccountId === accountId;
  });
}

export function useProjectMaterialRatesFetch({
  watchedProject,
  watchedCompanyId,
  watchedAccountId,
  setProjectMaterialRates,
}: {
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  watchedCompanyId: string | undefined;
  watchedAccountId: string | undefined;
  setProjectMaterialRates: (rates: MaterialRate[]) => void;
}): void {
  const { getCompanyMaterialRates } = useMaterialRates();
  const latestRequestKeyRef = useRef(0);
  // The project's whole rate list, before the customer narrowing. Held here so
  // that changing the customer re-narrows what is already in hand instead of
  // re-paging an identical dataset out of the API.
  const [fetchedProjectRates, setFetchedProjectRates] = useState<MaterialRate[]>(
    EMPTY_PROJECT_MATERIAL_RATES,
  );

  const projectId = watchedProject?.id;

  useEffect(() => {
    const requestKey = latestRequestKeyRef.current + 1;
    latestRequestKeyRef.current = requestKey;

    // Clear on the way IN to every transition, not just on deselection. The
    // request token only discards a late response; it does nothing about rates
    // already published, so the previous project's prices would otherwise sit
    // under the newly selected project until the new fetch resolved — labelled
    // as this project's pricing while belonging to another.
    setFetchedProjectRates(EMPTY_PROJECT_MATERIAL_RATES);

    if (!projectId || !watchedCompanyId) {
      return;
    }

    const loadProjectMaterialRates = async () => {
      try {
        const collectedRates: MaterialRate[] = [];
        let cursor: string | undefined;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await getCompanyMaterialRates({
            companyId: watchedCompanyId,
            projectIds: [projectId],
            pagination: {
              after: cursor,
              limit: MAX_API_PAGE_SIZE,
              page: 1,
            },
          });

          if (requestKey !== latestRequestKeyRef.current) {
            return;
          }

          collectedRates.push(...response.data);
          cursor = response.pagination.after ?? undefined;
          hasMorePages = Boolean(cursor);
        }

        setFetchedProjectRates(collectedRates);
      } catch (error) {
        if (requestKey !== latestRequestKeyRef.current) {
          return;
        }

        // Keeping the previous project's rates would label them as this
        // project's pricing, which is worse than showing no rate at all.
        setFetchedProjectRates(EMPTY_PROJECT_MATERIAL_RATES);

        try {
          connection.handleRequestError(
            error,
            t('error_messages.material_rates.failed_to_fetch_material_rates'),
          );
        } catch {
          // handleRequestError always rethrows after toasting and reporting;
          // the effect has nothing left to do with it.
        }
      }
    };

    void loadProjectMaterialRates();
  }, [getCompanyMaterialRates, projectId, watchedCompanyId]);

  useEffect(() => {
    setProjectMaterialRates(
      ratesApplicableToAccount(fetchedProjectRates, watchedAccountId),
    );
  }, [fetchedProjectRates, setProjectMaterialRates, watchedAccountId]);
}
